import App from "./App.vue";
import co2SubscriptDirective from "@/directives/co2SubscriptDirective.js";
import { createApp } from "vue";
import { createI18n } from "vue-i18n";
import customCardFourtyFourDirective from "./directives/customCardFourtyFourDirective.js";
import customCardZeroDirective from "./directives/customCardZeroDirective.js";
import customFont from "@/directives/customFontDirective";
import customSizesDirective from "./directives/customSizesDirective.js";
import customTitleDivDirective from "./directives/customTitleDivDirective.js";
import lineBreakDirective from "@/directives/lineBreakDirective.js";
import lineSpacingDirective from "@/directives/lineSpacingDirective.js";
import logoDirective from "./directives/logoDirective.js";
import miniSizeDirective from "./directives/miniSizeDirective";
import noBreakDirective from "./directives/noBreakDirective.js";
import printDirective from "./directives/printDirective.js";
import qrcodeDirective from "./directives/qrcodeDirective";
import textAlignDirective from "@/directives/textAlignDirective.js";
import textDirectionDirective from "@/directives/textDirectionDirective.js";

// Function to fetch JSON data
async function fetchData(url) {
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur lors de la requête :', error);
    return null;
  }
}

// Function to fetch translation from Tolgee via proxy
async function fetchTolgeeTranslation(languageCode) {
  const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;
  const url = `${apiBaseUrl}/api/translations/${languageCode}`;
  
  try {
    console.log(`Fetching ${languageCode} translations from Tolgee proxy...`);
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Tolgee proxy returned status ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Failed to fetch ${languageCode} from Tolgee:`, error.message);
    return null;
  }
}

// Function to extract language from URL
function getLanguageFromUrl() {
  // Extract pathname and split into parts
  const pathParts = window.location.pathname.split('/');
  
  // Look for language code in path parts
  for (const part of pathParts) {
    // Skip empty parts
    if (!part || part.length === 0) continue;
    
    // Check for language code format (xx_XX or xx-XX, 5 characters)
    if (part.length === 5 && (part.includes('_') || part.includes('-'))) {
      // Normalize to xx-XX format
      const normalized = part.replace('_', '-');
      
      // Check if this full code exists in our mapping
      if (lang2locale[normalized]) {
        return normalized;
      }
      
      // Check if the short code exists (e.g., "fr" from "fr-FR")
      const shortCode = normalized.split('-')[0];
      for (const [fullCode, short] of Object.entries(lang2locale)) {
        if (short === shortCode) {
          return fullCode;
        }
      }
    }
    // Also check for short language codes (e.g., "fr", "en", "es")
    else if (part.length === 2) {
      // Check if this short code exists in our mapping
      for (const [fullCode, short] of Object.entries(lang2locale)) {
        if (short === part) {
          return fullCode;
        }
      }
    }
  }
  
  return null; // No valid language code found
}

async function fetchLanguageData(languageCode) {
  // Always try to fetch from Tolgee first (with fallback to local files)
  const tolgeeData = await fetchTolgeeTranslation(languageCode);
  if (tolgeeData) {
    return tolgeeData;
  }
  
  // Fallback to local files if Tolgee fails or is not configured
  console.log(`Tolgee fallback to local files for ${languageCode}`);
  
  // Use absolute path from root to avoid issues with language prefix in URL
  const url = `/locales/${languageCode}.json`;
  return fetchData(url);
}

const lang2locale = {
  'de-DE': 'de',
  'en-GB': 'en',
  'fr-FR': 'fr',
  'ja-JP': 'ja',
  'vi-VN': 'vi',
  'my-MM': 'my',
  'pt-PT': 'pt',
  'ro-RO': 'ro',
  'sv-SE': 'sv',
  'fi-FI': 'fi',
  'nl-NL': 'nl',
  'es-ES': 'es',
  'es-LAT': 'lat',
  'fa-IR': 'fa',
  'id-ID': 'id',
  'tr-TR': 'tr',
  'hu-HU': 'hu',
  'pt-BR': 'br',
  'pl-PL': 'pl',
  'ca-ES': 'ca',
  'it-IT': 'it',
  'ru-RU': 'ru',
  'lt-LT': 'lt',
  'nb-NO': 'nb',
  'ar-AR': 'ar',
  'bg-BG': 'bg',
  'zh-CN': 'zh',
  'zh-TPE': 'tpe',
  'cs-CZ': 'cs',
  'en-US': 'us',
  'hy-AM': 'hy',
  'mf-MU': 'mf',
  'hr-HR': 'hr',
  'co-FR': 'co',
  'sr-SB' : 'sr',
  'ne-NP' : 'ne',
  'mk-MK' : 'mk',
  "et-EE" : 'et',
  "hi-IN" : 'hi',
  "el-GR" : 'el',
  "ml-IN" : 'ml',
  "th-TH" : 'th',
  "eo-EO" : 'eo',
  "km-kh" : 'kh',
  "cy-GB" : 'cy',
  "rw-RW" : "rw",
  "kk-KZ" : "kk",
  "he-IL" : "he",
  "zh-HK" : "hk",
  "mn-MN" : "mn",
  "da-DK" : "da",
  "zh-TW" : "tw",
  "ko-KR" : "ko",
  "tg-TJ" : "tg",
  "sl-SI" : "sl",
  "kn-IN" : "kn",
  "oc-ES" : "oc",
  "ka-GE" : "ka",
  "nl-NLB1" : "nlb",
  "bs-BA" : "bs"
}

// Get language from URL first to only fetch what we need
const urlLanguage = getLanguageFromUrl();
const defaultLocale = urlLanguage || "nlb"; // Use URL language or fallback to nlb
const shortLocale = lang2locale[defaultLocale] || "nlb";

console.log(`Language detection - URL language: ${urlLanguage}, Using: ${defaultLocale}, Short code: ${shortLocale}`);

// Only fetch the needed language + fallback language (French)
const languagesToFetch = [defaultLocale];
// Always include French as fallback
if (defaultLocale !== 'fr-FR') {
  languagesToFetch.push('fr-FR');
}

const fetchPromises = languagesToFetch.map((lang) => {
  return fetchLanguageData(lang);
});



Promise.all(fetchPromises)
  .then((languageDataArray) => {
    const messages = {};

    // Build messages object with only the languages we fetched
    languagesToFetch.forEach((lang, index) => {
      messages[lang2locale[lang]] = languageDataArray[index] || {};
    });
    
    // Create the i18n instance only after fetching data
    const i18n = createI18n({
      locale: shortLocale, // Use the short code for i18n
      fallbackLocale: "fr",
      messages: messages,
    });

    // Create Vue app with i18n instance
    const app = createApp(App);
    app.use(i18n);
    app.directive('co2-subscript', co2SubscriptDirective);
    app.directive('text-direction', textDirectionDirective);
    app.directive('text-align', textAlignDirective);
    app.directive('linebreak', lineBreakDirective);
    app.directive('linespacing', lineSpacingDirective);
    app.directive('customtitlediv', customTitleDivDirective);
    app.directive('logo', logoDirective);
    app.directive('print', printDirective);
    app.directive('qr-code', qrcodeDirective);
    app.directive('no-break', noBreakDirective);
    app.directive('card-zero', customCardZeroDirective);
    app.directive('card-fourtyfour', customCardFourtyFourDirective);
    app.directive('custom-size', customSizesDirective);
    app.directive('mini-size', miniSizeDirective);
    app.directive('custom-font', customFont);
    app.mount("#app");
  })
  .catch((error) => {
    console.error('Error fetching language data:', error);
  });
